/**
 * The upgrade block under an Explorer's 3 Cosmos picks (onboarding v2, spec
 * 13 Oct 2026): a line about seeing more, then "Start free trial" (with "No
 * card required" under it) and "Upgrade to Voyager".
 *
 * It is the `picks_block` upgrade prompt (admin → Billing → Upgrade prompts):
 * the copy is editable there and switching it off hides the block while the
 * 3 cards stay. Who sees it is the usual prompt rule (explorer, trial_lapsed).
 *
 * - Start free trial shows only while the account can start one (a lapsed
 *   trial cannot), and opens App's trial details sheet in place with the
 *   origin 'onboarding_picks_block' for attribution.
 * - Upgrade to Voyager opens the one plans sheet through openPlans, not a
 *   gate: nothing was blocked, so no paywall_shown. The sheet's own clicks
 *   carry locked_feature 'upgrade prompt: picks block'.
 *
 * Events: upgrade_prompt_shown / _click {placement:'picks_block', cta}, and
 * cosmos_picks_click_start_trial / cosmos_picks_click_upgrade, whose names
 * the checkout funnel already matches (*_click_start_trial, *_click_upgrade).
 */
import React, { useEffect, useState } from 'react';
import { trackClick } from '../../utils/analytics';
import { getCorpusStatsSync, loadCorpusStats } from '../../utils/corpusStats';
import { useUpgradeGate } from '../useUpgradeGate';
import { fillUpgradeText, promptFeature, trackUpgradePrompt, useUpgradePrompts } from '../upgrade/promptConfig';

export interface PicksTrialInfo {
  available?: boolean;
  days?: number;
  reason?: string;
}

export interface PicksUpgradeBlockProps {
  /** What the picks were built from, for the CTA events. */
  mode: string;
  industry: string | null;
  keyword: string | null;
  /** Live corpus size for {tech_count}; null falls back to the homepage stats. */
  techCount: number | null;
  /** The billing summary's trial payload (App's upgradeTrial). */
  trial?: PicksTrialInfo | null;
  /** Opens App's trial details sheet; `origin` lands on the trial row. */
  onStartTrial?: (origin: string) => void;
}

export const PicksUpgradeBlock: React.FC<PicksUpgradeBlockProps> = ({
  mode, industry, keyword, techCount, trial, onStartTrial,
}) => {
  const { config, viewer, shows } = useUpgradePrompts();
  const upgrade = useUpgradeGate();
  const on = shows('picks_block');
  // The facets count is the live one; until it arrives, the homepage stats
  // (cached for the session), and their built-in figure before that.
  const [statsCount, setStatsCount] = useState<number>(() => getCorpusStatsSync().technologies);
  useEffect(() => {
    if (techCount) return undefined;
    let live = true;
    loadCorpusStats().then(s => { if (live && s?.technologies) setStatsCount(s.technologies); }).catch(() => { /* keep */ });
    return () => { live = false; };
  }, [techCount]);
  useEffect(() => {
    if (on) trackUpgradePrompt('shown', 'picks_block', viewer.audience);
  }, [on, viewer.audience]);
  if (!on) return null;

  const b = config.picks_block;
  const count = techCount && techCount > 0 ? techCount : statsCount;
  const sub = fillUpgradeText((b.sub || '').split('{tech_count}').join(count.toLocaleString()), viewer);
  const trialOn = !!trial?.available && !!onStartTrial;
  const where = { mode, industry, keyword };

  const startTrial = () => {
    trackClick('cosmos_picks_click_start_trial', {
      ...where, trial_available: !!trial?.available, days: trial?.days ?? null,
    });
    trackUpgradePrompt('click', 'picks_block', viewer.audience, { cta: 'trial' });
    onStartTrial?.('onboarding_picks_block');
  };
  const openPlans = () => {
    trackClick('cosmos_picks_click_upgrade', where);
    trackUpgradePrompt('click', 'picks_block', viewer.audience, { cta: 'upgrade' });
    upgrade.openPlans(promptFeature('picks_block'));
  };

  return (
    <div role="region" aria-label="Upgrade" data-testid="picks-upgrade-block"
      className="mt-4 rounded-2xl border border-primary-200 bg-primary-100/60 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4">
      <div className="flex-1 min-w-0">
        <div className="text-[15px] font-semibold text-grayscale-1300 leading-snug">
          {fillUpgradeText(b.message, viewer)}
        </div>
        {sub && <div className="text-[13px] text-grayscale-1100 mt-1 leading-snug">{sub}</div>}
      </div>
      {/* Side by side from sm up; stacked full width on a phone, where the
          two labels do not fit one row. */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-start gap-2 sm:gap-3 shrink-0 w-full sm:w-auto">
        {trialOn && (
          <div className="flex flex-col items-center">
            <button type="button" onClick={startTrial}
              className="w-full sm:w-auto h-10 px-4 rounded-lg border border-primary-600 bg-grayscale-100 text-primary-700 text-sm font-semibold hover:bg-primary-100 whitespace-nowrap">
              {b.trial_cta}
            </button>
            {b.trial_note && <span className="text-[11px] text-grayscale-1000 mt-1 whitespace-nowrap">{b.trial_note}</span>}
          </div>
        )}
        <button type="button" onClick={openPlans}
          className="w-full sm:w-auto h-10 px-4 rounded-lg bg-primary-600 text-grayscale-100 text-sm font-semibold hover:bg-primary-700 shadow-sm whitespace-nowrap">
          {b.cta}
        </button>
      </div>
    </div>
  );
};

export default PicksUpgradeBlock;
